import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { LOAN_PRODUCTS } from "@/lib/loanProducts";
import ExportCsvButton from "@/components/ExportCsvButton";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const productTitle = (key) => LOAN_PRODUCTS.find((p) => p.key === key)?.title || key;

export default function AdminLoans() {
  const [loans, setLoans] = useState([]);
  useEffect(() => {
    api.get("/loans").then(({ data }) => setLoans(data || []));
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <DashboardLayout role="admin">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-ink sm:text-3xl">Loan portfolio</h1>
          <p className="mt-1 text-sm text-ink-soft">Every disbursed loan and its real-time health.</p>
        </div>
        <ExportCsvButton endpoint="loans.csv" filename="jrc_loans.csv" testId="loans-export-csv" />
      </div>

      <div className="overflow-hidden rounded-xl border border-[#E5E2DE] bg-white">
        <div className="hidden grid-cols-12 gap-4 border-b border-[#E5E2DE] bg-[#F9F6F0] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-muted md:grid">
          <div className="col-span-3">Borrower</div>
          <div className="col-span-3">Product</div>
          <div className="col-span-2">Principal / Balance</div>
          <div className="col-span-2">Progress</div>
          <div className="col-span-1">Arrears</div>
          <div className="col-span-1">Status</div>
        </div>
        {loans.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-ink-soft">No active loans yet.</div>
        ) : loans.map((l) => {
          const pct = Math.min(100, (l.amount_paid / l.total_repayable) * 100);
          const arrears = (l.schedule || []).filter((i) => i.status !== "paid" && i.due_date < today).length;
          return (
            <Link key={l.id} to={`/admin/loans/${l.id}`} data-testid={`admin-loan-row-${l.id}`}
              className="grid grid-cols-1 gap-2 border-b border-[#E5E2DE] px-5 py-4 transition-colors last:border-b-0 hover:bg-[#F9F6F0] md:grid-cols-12 md:gap-4">
              <div className="md:col-span-3">
                <div className="text-sm font-semibold text-ink">{l.applicant_name}</div>
                <div className="text-xs text-ink-muted">{l.applicant_email}</div>
              </div>
              <div className="text-sm text-ink md:col-span-3">{productTitle(l.product)}<div className="text-xs text-ink-muted">{l.interest_rate_pct}% · {l.term_months} mo</div></div>
              <div className="text-sm text-ink md:col-span-2">{fmt(l.principal)}<div className="text-xs text-ink-muted">Balance: {fmt(l.balance)}</div></div>
              <div className="md:col-span-2"><Progress value={pct} className="h-2" /></div>
              <div className="md:col-span-1 text-sm">{arrears > 0 ? <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-900">{arrears}</span> : <span className="text-ink-muted">—</span>}</div>
              <div className="md:col-span-1"><span className={`rounded-full border px-2 py-0.5 text-xs ${l.status === "active" ? "border-emerald-300 bg-emerald-100 text-emerald-900" : "border-violet-300 bg-violet-100 text-violet-900"}`}>{l.status}</span></div>
            </Link>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
